import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { User } from '@/types/auth'
import * as authApi from '@/api/auth'
import { safeStorage, STORAGE_KEYS } from '@/utils/storage'
import { debugLog } from '@/debug'

export const useAuthStore = defineStore('auth', () => {
  const user = ref<User | null>(null)
  const accessToken = ref<string | null>(null)
  const refreshTokenValue = ref<string | null>(null)
  const isLoading = ref(false)
  const error = ref<string | null>(null)

  const isAuthenticated = computed(() => !!accessToken.value)

  function clearStoredSession() {
    safeStorage.remove(STORAGE_KEYS.ACCESS_TOKEN)
    safeStorage.remove(STORAGE_KEYS.REFRESH_TOKEN)
    safeStorage.remove(STORAGE_KEYS.USER)
  }

  function loadSession() {
    const storedToken = safeStorage.get(STORAGE_KEYS.ACCESS_TOKEN)
    const storedRefresh = safeStorage.get(STORAGE_KEYS.REFRESH_TOKEN)
    const storedUser = safeStorage.get(STORAGE_KEYS.USER)
    if (!(storedToken && storedRefresh && storedUser)) return

    try {
      accessToken.value = storedToken
      refreshTokenValue.value = storedRefresh
      user.value = JSON.parse(storedUser)
    } catch (e) {
      debugLog.add('warn', `loadSession: parse failed: ${(e as Error).message}`)
      clearStoredSession()
    }
  }

  async function login(email: string, password: string) {
    isLoading.value = true
    error.value = null
    try {
      const data = await authApi.login({ email, password })
      accessToken.value = data.access_token
      refreshTokenValue.value = data.refresh_token
      user.value = data.user
      safeStorage.set(STORAGE_KEYS.ACCESS_TOKEN, data.access_token)
      safeStorage.set(STORAGE_KEYS.REFRESH_TOKEN, data.refresh_token)
      safeStorage.set(STORAGE_KEYS.USER, JSON.stringify(data.user))
    } catch (e) {
      error.value = 'auth.loginError'
      throw e
    } finally {
      isLoading.value = false
    }
  }

  async function logout() {
    try {
      if (refreshTokenValue.value) {
        await authApi.logout(refreshTokenValue.value)
      }
    } catch (e) {
      debugLog.add('warn', `logout API call failed: ${(e as Error).message}`)
    } finally {
      accessToken.value = null
      refreshTokenValue.value = null
      user.value = null
      clearStoredSession()
    }
  }

  return { user, accessToken, refreshTokenValue, isLoading, error, isAuthenticated, loadSession, login, logout }
})
